import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  ChevronDown,
  ChevronRight,
  AlertTriangle
} from 'lucide-react';

interface Execution {
  id: string;
  job_id: string;
  job_name: string;
  status: 'success' | 'failed' | 'running';
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  result: any;
  error_message: string | null;
  retry_count: number;
}

export default function ExecutionHistory() {
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [filteredExecutions, setFilteredExecutions] = useState<Execution[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'success' | 'failed' | 'running'>('all');
  const [filterJobId, setFilterJobId] = useState<string>('all');
  const [jobs, setJobs] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [executions, filterStatus, filterJobId]);

  const fetchData = async () => {
    try {
      const [executionsRes, jobsRes] = await Promise.all([
        axios.get(`/api/executions?limit=100&offset=0`),
        axios.get('/api/jobs'),
      ]);
      setExecutions(executionsRes.data);
      setJobs(jobsRes.data);
      setHasMore(executionsRes.data.length === 100);
      setError('');
    } catch (err) {
      setError('Failed to load execution history');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...executions];

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter((exec) => exec.status === filterStatus);
    }

    // Job filter
    if (filterJobId !== 'all') {
      filtered = filtered.filter((exec) => exec.job_id === filterJobId);
    }

    setFilteredExecutions(filtered);
  };

  const loadMore = async () => {
    try {
      const response = await axios.get(`/api/executions?limit=100&offset=${executions.length}`);
      const newExecutions = response.data;
      setExecutions([...executions, ...newExecutions]);
      setHasMore(newExecutions.length === 100);
    } catch (err) {
      console.error('Failed to load more executions:', err);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-400" />;
      case 'running':
        return <Loader className="w-5 h-5 text-yellow-400 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-slate-400" />;
    }
  };

  const formatDuration = (ms: number | null) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
    return `${(ms / 60000).toFixed(2)}m`;
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="text-white text-center">Loading execution history...</div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Execution History</h1>
        <p className="text-slate-400">View past job executions and their results</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 text-red-200">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="running">Running</option>
            </select>
          </div>

          {/* Job Filter */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Job
            </label>
            <select
              value={filterJobId}
              onChange={(e) => setFilterJobId(e.target.value)}
              className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Jobs</option>
              {jobs.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
          <p className="text-sm text-slate-400 mb-1">Total Executions</p>
          <p className="text-2xl font-bold text-white">{filteredExecutions.length}</p>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
          <p className="text-sm text-slate-400 mb-1">Successful</p>
          <p className="text-2xl font-bold text-green-400">
            {filteredExecutions.filter((e) => e.status === 'success').length}
          </p>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
          <p className="text-sm text-slate-400 mb-1">Failed</p>
          <p className="text-2xl font-bold text-red-400">
            {filteredExecutions.filter((e) => e.status === 'failed').length}
          </p>
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
          <p className="text-sm text-slate-400 mb-1">Running</p>
          <p className="text-2xl font-bold text-yellow-400">
            {filteredExecutions.filter((e) => e.status === 'running').length}
          </p>
        </div>
      </div>

      {/* Executions List */}
      <div className="space-y-3">
        {filteredExecutions.length === 0 ? (
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-12 text-center">
            <Clock className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No execution history found</p>
          </div>
        ) : (
          filteredExecutions.map((execution) => (
            <div
              key={execution.id}
              className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden"
            >
              {/* Summary Row */}
              <button
                onClick={() => toggleExpand(execution.id)}
                className="w-full p-4 flex items-center justify-between hover:bg-slate-700/50 transition-colors text-left"
              >
                <div className="flex items-center gap-4 flex-1">
                  {getStatusIcon(execution.status)}

                  <div className="flex-1">
                    <h3 className="text-white font-medium">{execution.job_name}</h3>
                    <p className="text-sm text-slate-400">
                      Started: {new Date(execution.started_at).toLocaleString()}
                    </p>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-slate-400">Duration</p>
                      <p className="text-white font-medium">{formatDuration(execution.duration_ms)}</p>
                    </div>

                    {execution.retry_count > 0 && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-yellow-900/50 text-yellow-300 rounded text-xs">
                        <AlertTriangle className="w-3 h-3" />
                        <span>{execution.retry_count} retries</span>
                      </div>
                    )}

                    <span
                      className={`px-3 py-1 rounded text-xs font-medium ${
                        execution.status === 'success'
                          ? 'bg-green-900/50 text-green-300'
                          : execution.status === 'failed'
                          ? 'bg-red-900/50 text-red-300'
                          : 'bg-yellow-900/50 text-yellow-300'
                      }`}
                    >
                      {execution.status.toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className="ml-4 text-slate-400">
                  {expandedId === execution.id ? (
                    <ChevronDown className="w-5 h-5" />
                  ) : (
                    <ChevronRight className="w-5 h-5" />
                  )}
                </div>
              </button>

              {/* Expanded Details */}
              {expandedId === execution.id && (
                <div className="border-t border-slate-700 p-4 bg-slate-900">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-slate-400 mb-1">Execution ID</p>
                      <code className="text-sm text-slate-300 bg-slate-800 px-2 py-1 rounded font-mono">
                        {execution.id}
                      </code>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400 mb-1">Job ID</p>
                      <code className="text-sm text-slate-300 bg-slate-800 px-2 py-1 rounded font-mono">
                        {execution.job_id}
                      </code>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400 mb-1">Started At</p>
                      <p className="text-white">{new Date(execution.started_at).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400 mb-1">Finished At</p>
                      <p className="text-white">
                        {execution.finished_at
                          ? new Date(execution.finished_at).toLocaleString()
                          : 'Still running...'}
                      </p>
                    </div>
                  </div>

                  {execution.error_message && (
                    <div className="mb-4">
                      <p className="text-sm text-slate-400 mb-2">Error Message</p>
                      <div className="bg-red-900/20 border border-red-700 rounded-lg p-3">
                        <pre className="text-sm text-red-300 whitespace-pre-wrap font-mono">
                          {execution.error_message}
                        </pre>
                      </div>
                    </div>
                  )}

                  {execution.result && (
                    <div>
                      <p className="text-sm text-slate-400 mb-2">Result</p>
                      <div className="bg-slate-800 rounded-lg p-3 max-h-96 overflow-auto">
                        <pre className="text-sm text-slate-300 whitespace-pre-wrap font-mono">
                          {JSON.stringify(execution.result, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Load More */}
      {hasMore && filteredExecutions.length > 0 && (
        <div className="flex justify-center">
          <button
            onClick={loadMore}
            className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
          >
            Load More
          </button>
        </div>
      )}
    </div>
  );
}
